// Stimulate's panel sections (ba-web): the field's colour bar, peak and control, then the source: for TMS the coil's facts
// with the patch its live basis holds in, which is also the drag pad (pointer or arrow keys, clamped with the edge shown;
// ba-stim's sampler and projector, pipelines/stim/browser, STIM-001/002/008, give the field and the pose at each offset from
// the subject's grid bundle, the scene's second sim slot, and both go to the viewport together as features/stim.ts arranges);
// for tDCS the montage: the pads the FEM put on the scalp, their currents, and a current control that scales the fixed
// montage's field. Numbers come from ba-app's stim-sim-v0 reader on the runs the scene has open; a basis (15–33 MB of signed
// vectors) opens on the first touch, or for a link with an offset. The drag's clamps, keys and words are the chosen bundle's.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useAtlas } from '../app/store.ts';
import { openedSlot } from '../app/sims.ts';
import type { StimCoil, StimElectrode, StimHeader, StimPose } from '../data/sims/types.ts';
import { getViewport } from '../viewport/host.ts';
import { useLoaded } from '../ui/hooks.ts';
import { Section, Slider, Switch } from '../ui/primitives.tsx';
import { atLimit, barTop, basisFacts, clampOffset, clearCoil, clearMontage, currentControl, currentScale, DEFAULT_CURRENT, DEFAULT_PATCH, drawnAsHdCortex, drawsMeshes, FIELD_RANGE, fieldBarCss, fieldRange, INTENSITY, isTdcs, liveUpdate, MONTAGE_LOOK, openLive, patchOf, patchText, poseFacts, showCoil, showField, showMontage, type BasisFacts, type FieldPath, type LiveBasis, type LivePeak, type LiveViewport, type Offset, type Patch, useStim, whereOf } from './stim.ts';

interface Facts {
  peak: { value: number; vertex: number };
  where: string;
  coil: StimCoil | null;
  pose: StimPose | null;
  dipoles: number;
  coilPositions: Float32Array | null;
}

async function loadFacts(path: string): Promise<Facts | null> {
  const o = openedSlot('main');
  if (!o || o.format !== 'stim' || o.path !== path) return null;
  const sim = o.sim;
  const [peak, coil] = await Promise.all([sim.peak(), sim.coil()]);
  const d = sim.header.arrays.coil_positions;
  const coilPositions = d ? ((await sim.files.array(d)) as Float32Array) : null;
  return { peak, where: whereOf(sim, peak.vertex), coil, pose: sim.defaultPose, dipoles: d?.shape[0] ?? 0, coilPositions };
}

/** The grid bundle's own account of its basis: its poses and patch from the header, its validated error from the header or
 *  the validation file it names (Ernie's STIM-001 median lives there). */
async function loadBasisFacts(gridPath: string): Promise<BasisFacts | null> {
  const o = openedSlot('grid');
  if (!o || o.format !== 'stim' || o.path !== gridPath) return null;
  const h = o.sim.header;
  const rel = h.basis?.validation;
  const file = typeof rel === 'string' ? await o.sim.files.maybeJson<Record<string, unknown>>(rel).catch(() => null) : null;
  return basisFacts(h, file);
}

const fmtMm = (v: number) => `${v < 0 ? '−' : ''}${Math.abs(v).toFixed(0)}`;
const fmtOff = (v: number) => `${v < 0 ? '−' : '+'}${Math.abs(v).toFixed(1)}`;
const fmtPct = (v: number) => `${(v * 100).toFixed(1)} %`;

type LiveStatus = 'idle' | 'loading' | 'ready' | 'error';

/** The pad's 120-unit view box: the patch is its middle 80 units, C3 (the patch's centre) at 60, 60, y up. */
const padScale = (p: Patch): { ux: number; uy: number; cx: number; cy: number } => ({
  ux: 80 / (p[0][1] - p[0][0]),
  uy: 80 / (p[1][1] - p[1][0]),
  cx: (p[0][0] + p[0][1]) / 2,
  cy: (p[1][0] + p[1][1]) / 2,
});
const toOffset = (el: Element, clientX: number, clientY: number, patch: Patch): Offset => {
  const r = el.getBoundingClientRect();
  const px = ((clientX - r.left) / r.width) * 120;
  const py = ((clientY - r.top) / r.height) * 120;
  const { ux, uy, cx, cy } = padScale(patch);
  return [cx + (px - 60) / ux, cy + (60 - py) / uy];
};

export function StimSections() {
  const main = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.slots.find((x) => x.slot === 'main' && x.format === 'stim') : undefined));
  const grid = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.slots.find((x) => x.slot === 'grid' && x.format === 'stim') : undefined));
  const intensity = useStim((s) => s.intensity);
  const setIntensity = useStim((s) => s.setIntensity);
  const current = useStim((s) => s.current);
  const setCurrent = useStim((s) => s.setCurrent);
  const offset = useStim((s) => s.offset);
  const setOffset = useStim((s) => s.setOffset);
  const path = main?.path;
  const gridPath = grid?.path;
  // The run's kind decides the source shown and the control: a coil's slew rate, or a montage's current.
  const header = main?.format === 'stim' ? (main.header as unknown as StimHeader) : null;
  const tdcs = !!header && isTdcs(header);
  const control = useMemo(() => (header ? currentControl(header) : DEFAULT_CURRENT), [header]);
  const range = useMemo(() => (header ? fieldRange(header) : FIELD_RANGE), [header]);
  const hi = range[1];
  /** |E|'s multiplier: the slew rate in MA/s (TMS, the run's own 1), or current over the montage's reference (tDCS). */
  const k = tdcs ? currentScale(current, control) : intensity;
  const gridHeader = grid?.format === 'stim' ? (grid.header as unknown as StimHeader) : null;
  const patch = useMemo(() => (gridHeader ? patchOf(gridHeader) : DEFAULT_PATCH), [gridHeader]);
  const facts = useLoaded(() => (path ? loadFacts(path) : null), [path]);
  const basis = useLoaded(() => (gridPath ? loadBasisFacts(gridPath) : null), [gridPath]);
  // Once the scene's runs are known: a head with no live basis keeps the coil at its one pose, so an offset left over from
  // another head's drag goes; with a basis, the offset is held inside this bundle's patch (a link's arrives clamped to
  // Ernie's ±10 mm before the bundle is known). Not before: the sections mount ahead of the runs, and a reset then would
  // throw a link's `coil=` away.
  useEffect(() => {
    if (!path) return;
    if (!grid) {
      if (offset[0] !== 0 || offset[1] !== 0) setOffset([0, 0]);
      return;
    }
    const c = clampOffset(offset, patch);
    if (c[0] !== offset[0] || c[1] !== offset[1]) setOffset(c, patch);
  }, [path, grid, offset, patch, setOffset]);
  const [drawn, setDrawn] = useState<FieldPath | null>(null);
  const made = useRef({ layer: false });
  // The field layer lives on the head's cortex surfaces, and a pack load removes every layer: the layer is made again
  // once the head is in. IXI 165's cortex and its parent head load slower than Ernie's, so a layer sent before the
  // load replied was refused and nothing drew the field (02:40 UTC 10-03).
  const loadStatus = useAtlas((s) => s.load.status);
  useEffect(() => {
    if (loadStatus !== 'ready') made.current.layer = false;
  }, [loadStatus]);
  const vpRef = useRef<LiveViewport | null>(null);
  const live = useRef<LiveBasis | null>(null);
  const opening = useRef(false);
  const frame = useRef(0);
  const [liveStatus, setLiveStatus] = useState<LiveStatus>('idle');
  const [liveError, setLiveError] = useState<string | null>(null);
  const [livePeak, setLivePeak] = useState<LivePeak | null>(null);
  const [dragging, setDragging] = useState(false);
  const drag = useRef(false);

  useEffect(() => {
    let on = true;
    void getViewport()?.then((v) => {
      if (on) vpRef.current = v as LiveViewport;
    });
    return () => {
      on = false;
    };
  }, []);

  // The coil, when the viewport takes meshes: the casing, with the dipoles inside it while the Source model is on; sent
  // again when the head has loaded (a load clears the scene) and when the switch moves. A tDCS run has no coil pose.
  const sourceModel = useStim((s) => s.sourceModel);
  const setSourceModel = useStim((s) => s.setSourceModel);
  const coilDrawn = useStim((s) => s.coil);
  const setCoil = useStim((s) => s.setCoil);
  useEffect(() => {
    if (!path || loadStatus !== 'ready') return;
    made.current.layer = false;
    let on = true;
    void (async () => {
      const o = openedSlot('main');
      const vp = (await getViewport()) as LiveViewport | null;
      if (!on || !vp || !o || o.format !== 'stim') return;
      const f = facts.value;
      if (f?.coilPositions && f.pose?.matrix) {
        const drawn = await showCoil(vp, f.coilPositions, f.pose.matrix, sourceModel).catch(() => null);
        if (on) setCoil(drawn);
      }
    })();
    return () => {
      on = false;
    };
  }, [path, facts.value, sourceModel, loadStatus, setCoil]);

  // The montage, when the viewport takes meshes: a tDCS run's pads and scalp go on once the head is in, and the coil of a
  // TMS run shown before on the same head comes off (a run switch on one head loads nothing, so nothing clears the scene);
  // a TMS run takes a montage left by a tDCS run away.
  const montage = useStim((s) => s.montage);
  const setMontage = useStim((s) => s.setMontage);
  useEffect(() => {
    if (!path || loadStatus !== 'ready') return;
    let on = true;
    void (async () => {
      const o = openedSlot('main');
      const vp = (await getViewport()) as LiveViewport | null;
      if (!on || !vp || !o || o.format !== 'stim' || o.path !== path) return;
      if (isTdcs(o.sim.header)) {
        if (!drawsMeshes(vp)) {
          if (on) setMontage({ pads: [], scalp: false });
          return;
        }
        await clearCoil(vp);
        if (on) setCoil(null);
        // The run's own scalp is the montage's ghost head again: ba-stim's -v2 runs carry a display scalp defaced with the shared
        // IXI165 face mask (#1054; the lead, 10-04), so #974's hide, which kept the CHARM run's face off the screen, is over.
        const shown = await showMontage(vp, o.sim).catch((err: unknown) => {
          console.warn('[stim] montage', err);
          return { pads: [], scalp: false };
        });
        if (on) setMontage(shown);
      } else {
        const prev = useStim.getState().montage;
        if (prev) {
          if (prev.pads.length || prev.scalp) await clearMontage(vp, prev.pads);
          if (on) setMontage(null);
        }
      }
    })();
    return () => {
      on = false;
    };
  }, [path, loadStatus, setMontage, setCoil]);

  // The saved field at this multiplier, once the head is in, until the live basis takes over the pushes; the colours over
  // the run's own range (the grid's fixed [0, 2] V/m for TMS, a tDCS run's own), so the layer is made again on a run switch.
  useEffect(() => {
    if (!path || live.current || loadStatus !== 'ready') return;
    let on = true;
    void (async () => {
      const o = openedSlot('main');
      const vp = (await getViewport()) as LiveViewport | null;
      if (!on || !vp || !o || o.format !== 'stim' || o.path !== path) return;
      const p = await showField(vp, o.sim, k, made.current, drawnAsHdCortex(useAtlas.getState().brain, o.sim.header), fieldRange(o.sim.header)).catch((err: unknown) => {
        console.warn('[stim] field', err);
        return 'none' as const;
      });
      if (on) setDrawn(p);
    })();
    return () => {
      on = false;
    };
  }, [path, k, loadStatus]);

  // Another scene's runs, or the scene reopened: the basis goes with the old runs.
  useEffect(() => {
    live.current = null;
    opening.current = false;
    setLiveStatus('idle');
    setLivePeak(null);
    setLiveError(null);
  }, [gridPath]);

  /** One live step with the latest offset and intensity, at most once a frame: pose and field together. */
  const step = useCallback(() => {
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      const b = live.current;
      if (!b) return;
      const st = useStim.getState();
      setLivePeak(liveUpdate(vpRef.current ?? {}, b, st.offset, st.intensity, st.coil ?? undefined));
    });
  }, []);

  /** The basis, opened once per grid run: the saved field's layer first, so the live pushes have a layer to land on. */
  const ensureLive = useCallback(() => {
    if (live.current || opening.current || !gridPath) return;
    opening.current = true;
    setLiveStatus('loading');
    setLiveError(null);
    void (async () => {
      try {
        const o = openedSlot('grid');
        if (!o || o.format !== 'stim' || o.path !== gridPath) throw new Error('the grid bundle isn’t open');
        const vp = vpRef.current ?? ((await getViewport()) as LiveViewport | null);
        const m = openedSlot('main');
        if (vp && m && m.format === 'stim' && !made.current.layer)
          await showField(vp, m.sim, useStim.getState().intensity, made.current, drawnAsHdCortex(useAtlas.getState().brain, m.sim.header), fieldRange(m.sim.header)).catch(() => 'none' as const);
        const b = await openLive(o.sim);
        if (openedSlot('grid')?.path !== gridPath) return;
        live.current = b;
        setLiveStatus('ready');
        step();
      } catch (e) {
        setLiveStatus('error');
        setLiveError(e instanceof Error ? e.message : String(e));
      } finally {
        opening.current = false;
      }
    })();
  }, [gridPath, step]);

  // Every change of offset or intensity is a live step once the basis is open; a link's offset opens it.
  useEffect(() => {
    if (live.current) step();
    else if (offset[0] !== 0 || offset[1] !== 0) ensureLive();
  }, [offset, intensity, ensureLive, step]);
  useEffect(
    () => () => {
      if (frame.current) cancelAnimationFrame(frame.current);
    },
    [],
  );

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    e.currentTarget.focus();
    drag.current = true;
    setDragging(true);
    setOffset(toOffset(e.currentTarget, e.clientX, e.clientY, patch), patch);
    ensureLive();
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    setOffset(toOffset(e.currentTarget, e.clientX, e.clientY, patch), patch);
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    drag.current = false;
    setDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const d = e.shiftKey ? 5 : 1;
    const [x, y] = offset;
    let next: Offset | null = null;
    switch (e.key) {
      case 'ArrowLeft':
        next = [x - d, y];
        break;
      case 'ArrowRight':
        next = [x + d, y];
        break;
      case 'ArrowUp':
        next = [x, y + d];
        break;
      case 'ArrowDown':
        next = [x, y - d];
        break;
      case 'Home':
        next = [0, 0];
        break;
      default:
        return;
    }
    e.preventDefault();
    e.stopPropagation();
    setOffset(next, patch);
    ensureLive();
  };

  if (!path) return null;
  const f = facts.value;
  const pose = poseFacts(f?.pose ?? null);
  const [ox, oy] = offset;
  const moved = ox !== 0 || oy !== 0;
  const lim = atLimit(offset, patch);
  const atEdge = lim.x || lim.y;
  const mainSim = openedSlot('main');
  const shownPeak = livePeak && liveStatus === 'ready' ? livePeak.value : f ? f.peak.value * k : null;
  const top = barTop(shownPeak, hi);
  const decimals = tdcs ? 3 : 2;
  const at = tdcs ? `${current.toFixed(1)} ${control.unit}` : `${intensity.toFixed(2)} ${INTENSITY.unit}`;
  const peakText =
    livePeak && liveStatus === 'ready'
      ? `${livePeak.value.toFixed(2)} V/m peak ${mainSim?.format === 'stim' ? whereOf(mainSim.sim, livePeak.vertex) : 'on the cortex'} at ${at}, coil ${fmtOff(ox)}, ${fmtOff(oy)} mm from C3`
      : f
        ? `${(f.peak.value * k).toFixed(decimals)} V/m peak ${f.where} at ${at}`
        : (facts.error ?? 'Reading the field');
  // The basis in the bundle's own words: its study, poses, patch and validated error, once its header is read.
  const bf = basis.value;
  const within = `within ${patchText(patch)} of C3`;
  const err = bf?.medianError !== null && bf?.medianError !== undefined ? `median error ${fmtPct(bf.medianError)}` : null;
  const basisLine = bf ? `the live basis (${bf.study ?? 'validated'}, ${bf.poses} FEM poses) holds ${within}${err ? `, ${err}` : ''}` : `the live basis holds ${within}`;
  const caption =
    liveStatus === 'loading'
      ? `Opening the live basis: ${bf ? `${bf.poses} FEM poses` : 'the FEM poses'} as signed vectors…`
      : liveStatus === 'error'
        ? `The live basis didn’t open: ${liveError ?? 'unknown'}`
        : liveStatus === 'ready'
          ? `${atEdge ? `At the patch’s edge: the coil stays ${within}. ` : ''}Live field: bilinear in the ${bf ? `${bf.poses} ` : ''}poses’ signed vectors, no extrapolation${bf ? ` (${bf.study ?? 'validated'}${err ? `, ${err}` : ''})` : ''}, the coil keeping its orientation.${drawn === 'none' ? ' The picture waits on the engine’s vertex layer; the numbers are live.' : ''}`
          : `Drag the coil, or use the arrow keys: ${basisLine}, the coil keeping its orientation.`;
  const { ux, uy, cx, cy } = padScale(patch);
  const electrodes: StimElectrode[] = tdcs ? (header?.electrodes ?? []) : [];
  const padsDrawn = montage ? montage.pads.length : 0;
  return (
    <>
      <Section title="Field">
        <div className="colorbar stim-bar" role="img" aria-label={`Electric field, 0 to ${hi} V/m${top.saturated ? `, values above shown as the top colour` : ''}`}>
          <span className="name ba-type-small">|E| · V/m</span>
          <span className="strip stim-strip" style={{ background: fieldBarCss() }}>
            {shownPeak !== null && Number.isFinite(shownPeak) ? <span className="stim-tick" style={{ left: `${(Math.min(1, Math.max(0, shownPeak / hi)) * 100).toFixed(1)}%` }} title={`The peak shown, ${shownPeak.toFixed(decimals)} V/m`} /> : null}
          </span>
          <span className="ticks ba-type-caption">
            <span>0</span>
            <span title={top.saturated ? `The peak shown is above ${hi} V/m; the colour clamps at the top` : undefined}>{top.label}</span>
          </span>
        </div>
        <p className="stim-peak ba-type-small num">{peakText}</p>
        <div className="rows">
          {tdcs ? (
            <label className="row stim-intensity stim-current">
              <span>Current</span>
              <Slider value={current} min={control.min} max={control.max} step={control.step} onChange={(v) => setCurrent(v, control)} label="Current" />
              <span className="num value">{`${current.toFixed(1)} ${control.unit}`}</span>
            </label>
          ) : (
            <label className="row stim-intensity">
              <span>Intensity</span>
              <Slider value={intensity} min={INTENSITY.min} max={INTENSITY.max} step={INTENSITY.step} onChange={setIntensity} label="Intensity" />
              <span className="num value">{`${intensity.toFixed(2)} ${INTENSITY.unit}`}</span>
            </label>
          )}
        </div>
        <p className="ba-type-caption muted stim-note">
          {tdcs
            ? `At this fixed montage and these conductivities the field is linear in the current, so the current rescales |E| (${(f ? f.peak.value : 0).toFixed(3)} V/m peak at ${control.reference.toFixed(1)} ${control.unit}). Colours show V/m, not activation or a threshold.`
            : 'The field scales with the coil current’s slew rate, so intensity rescales |E|. Colours show V/m, not activation or a motor threshold.'}
          {' The cortex is coloured by |E| in the legend’s colormap; below 5 % of the legend’s range the field is not drawn; the shading is the surface’s own.'}
          {drawn === 'none' ? ` This viewport doesn’t draw the field yet; the numbers are ${liveStatus === 'ready' ? 'the live basis’s' : 'the run’s'}.` : ''}
          {top.saturated ? ` Values above ${hi} V/m show as the top colour.` : ''}
        </p>
      </Section>
      {tdcs ? (
        <Section title="Montage">
          <dl className="stim-facts stim-montage ba-type-small">
            {electrodes.map((e) => {
              const a = e.current_A ?? 0;
              const anode = a >= 0;
              const mA = Math.abs(a) * 1000 * k;
              const dims = e.dimensions ? ` · ${e.dimensions[0]} × ${e.dimensions[1]} mm pad` : '';
              return (
                <Fragment key={e.id}>
                  <dt>
                    <span className="stim-swatch" style={{ background: anode ? MONTAGE_LOOK.anode : MONTAGE_LOOK.cathode }} aria-hidden="true" />
                    {anode ? 'Anode' : 'Cathode'}
                  </dt>
                  <dd>{`${e.centre ?? e.id} · ${anode ? '+' : '−'}${mA.toFixed(1)} mA${dims}`}</dd>
                </Fragment>
              );
            })}
            <dt>Pads</dt>
            <dd>{electrodes[0]?.thickness !== undefined ? `${electrodes[0].thickness} mm gel or sponge on the FEM’s own scalp, equipotential` : 'The FEM’s own electrodes on its scalp'}</dd>
            <dt>Field</dt>
            <dd>Direct current, static: the FEM’s |E| on this cortex at the current set</dd>
          </dl>
          <p className="ba-type-caption muted stim-coil-note stim-montage-note">
            {padsDrawn === electrodes.length && electrodes.length > 0
              ? `The pads are the FEM’s own electrode boundaries, drawn where the model put them in the head’s millimetres${montage?.scalp ? ', on the FEM’s scalp as a ghost' : '; the FEM’s own scalp isn’t drawn here'}; the field is the FEM’s.`
              : padsDrawn > 0
                ? `This viewport drew ${padsDrawn} of the ${electrodes.length} pads; the field is the FEM’s.`
                : 'This viewport doesn’t draw the pads; the numbers are the run’s.'}
          </p>
        </Section>
      ) : (
        <Section title="Coil">
          <dl className="stim-facts ba-type-small">
            <dt>Model</dt>
            <dd>{f?.coil?.name ?? 'Magstim 70 mm figure-eight'}</dd>
            <dt>Over</dt>
            <dd>{pose.centre ? `C3, left motor cortex · centre ${fmtMm(pose.centre[0])}, ${fmtMm(pose.centre[1])}, ${fmtMm(pose.centre[2])} mm` : 'C3, left motor cortex'}</dd>
            <dt>Gap</dt>
            <dd>{pose.gapMm !== null ? `${pose.gapMm} mm off the scalp, nominal · handle toward CP3` : '—'}</dd>
            <dt>Source</dt>
            <dd>{f ? `${f.dipoles.toLocaleString()} dipoles, the FEM’s own source model` : '—'}</dd>
          </dl>
          <div className="rows">
            <label className="row stim-source">
              <span>Source model</span>
              <Switch checked={sourceModel} onChange={setSourceModel} label="Show the FEM’s source dipoles inside the coil’s casing" />
            </label>
          </div>
          <p className="ba-type-caption muted stim-coil-note">
            {coilDrawn?.casing
              ? `The casing is a figure-eight shell from the dipoles’ extent, not the device’s own geometry; the field is the FEM’s.${sourceModel ? ' The dots inside it are the source model the FEM used.' : ''}`
              : coilDrawn?.dipoles
                ? 'This viewport draws the coil as the FEM’s source dipoles; its casing waits on a newer viewport.'
                : 'This viewport doesn’t draw the coil; the numbers are the run’s.'}
          </p>
          {!grid ? (
            <p className="ba-type-caption muted stim-note stim-one-pose">One pose for this head: the coil at C3, with no live basis to drag it on. The field is the FEM’s own.</p>
          ) : (
            <figure className={`stim-box${dragging ? ' dragging' : ''}${atEdge ? ' limit' : ''}`}>
              <div
                className="stim-pad"
                role="slider"
                tabIndex={0}
                aria-label="Coil offset from C3 along the scalp, millimetres"
                aria-roledescription="two-dimensional slider"
                aria-valuemin={patch[0][0]}
                aria-valuemax={patch[0][1]}
                aria-valuenow={ox}
                aria-valuetext={`${fmtOff(ox)} mm across, ${fmtOff(oy)} mm along${atEdge ? ', at the edge of the patch' : ''}`}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onKeyDown={onKeyDown}
              >
                <svg viewBox="0 0 120 120" width="120" height="120" aria-hidden="true">
                  <rect x="20" y="20" width="80" height="80" rx="3" />
                  <line x1="60" y1="20" x2="60" y2="100" />
                  <line x1="20" y1="60" x2="100" y2="60" />
                  <circle cx="60" cy="60" r="1.5" className="c3dot" />
                  <circle cx={60 + (ox - cx) * ux} cy={60 - (oy - cy) * uy} r="4" className="coil" />
                  <text x="60" y="13" textAnchor="middle">
                    {`${patch[1][1] < 0 ? '−' : '+'}${Math.abs(patch[1][1])} mm`}
                  </text>
                  <text x="60" y="114" textAnchor="middle">
                    {`${patch[1][0] < 0 ? '−' : '+'}${Math.abs(patch[1][0])} mm`}
                  </text>
                  <text x="66" y="57" className="c3">
                    C3
                  </text>
                </svg>
              </div>
              <div className="stim-live">
                <p className="stim-offset ba-type-small num">{moved ? `Coil ${fmtOff(ox)}, ${fmtOff(oy)} mm from C3` : 'Coil at C3'}</p>
                {moved ? (
                  <button type="button" className="link-btn ba-type-small" onClick={() => setOffset([0, 0], patch)}>
                    Back to C3
                  </button>
                ) : null}
              </div>
              <figcaption className="ba-type-caption muted">{caption}</figcaption>
            </figure>
          )}
        </Section>
      )}
    </>
  );
}
